import React, {useEffect} from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useModels from '../../hooks/useModels'
import Page from '../../components/Page'
import ItemLoader, {RendererProps} from '../../components/ItemLoader'
import ConnectModels from '../../components/connections/ConnectModels'
import CurrentConnections from '../../components/connections/Current'
import type {Evaluation as EvaluationType} from '../../services/types'
import {ModelFilters} from '../../components/filters'

const RenderEvaluationConnections = ({
  item: evaluation,
  setItem,
}: RendererProps<EvaluationType>) => {
  const {refresh, getItems} = useModels()
  useEffect(
    () =>
      refresh({
        filters: {
          public_useable: true,
          evaluation: evaluation.id,
          fields: [
            'cost_per_input_character_usd',
            'cost_per_output_character_usd',
            'cost_per_instance_hour_usd',
            'name',
            'description',
            'publisher',
            'next_run',
            'score',
            'architecture',
            'modalities',
            'num_parameters',
            'price',
            'statistics',
          ],
        },
      }),
    [evaluation.id, refresh]
  )
  return (
    <Page title={`${evaluation.name}: Evaluate Models`} showTitle>
      <Stack direction="column" sx={{pt: 5}} spacing={3}>
        <Typography>
          Welcome to our evaluation ordering page, where you have the flexibility to select the
          models you wish to evaluate based on your specific requirements. The price you see listed
          is comprehensive, encompassing all associated costs such as model fees, infrastructure,
          and other related expenses, ensuring no hidden charges.
        </Typography>
        <Typography>
          Additionally, we offer the convenience of scheduling evaluations to occur daily, weekly,
          every two weeks, monthly, or quarterly according to your needs. Please note that the price
          displayed is for a single evaluation run. Each subsequent run you schedule will
          automatically deduct the same amount from your payment account on file, maintaining a
          seamless and predictable billing process. You can cancel scheduled runs whenever you want
          (they will appear on this page, once ordered).
        </Typography>
      </Stack>
      <CurrentConnections item={evaluation} itemsType="model" />
      <ConnectModels
        item={evaluation}
        itemType="evaluation"
        itemsGetter={getItems}
        searchOptions={{
          searchLabel: 'filter models',
          sortLabel: 'Sort by:',
          sortOptions: {
            scoreDesc: 'Top average score',
            scoreAsc: 'Bottom average score',
            priceDesc: 'Most expensive',
            priceAsc: 'Least expensive',
            nameAsc: 'Model name (asc)',
            nameDesc: 'Model name (desc)',
            publisherAsc: 'Publisher (asc)',
            publisherDesc: 'Publisher (desc)',
          },
          filtersElem: ModelFilters,
        }}
      />
    </Page>
  )
}

const EvaluationConnections: React.FC = () => (
  <ItemLoader itemName="evaluation" renderer={RenderEvaluationConnections} />
)
export default EvaluationConnections
